import { useQueryClient } from '@tanstack/react-query';
import { useAxiosInfiniteQuery, useAxiosMutation, useAxiosQuery } from './use-axios.hook';
import { UpdateArcgisCivil3dFeatureLineSettingApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/feature-lines/update-arcgis-civil3d-feature-line-setting.request';
import { CreateArcgisCivil3dFeatureLineSettingApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/feature-lines/create-arcgis-civil3d-feature-line-setting.request';
import {
  updateFeatureLineSetting,
  createFeatureLineSetting,
  createPropertyMappingForFeatureLine,
  getFeatureLineSetting,
  deleteFeatureLineSettings,
  deletePropertyMappingFromFeatureLine,
  getAllPropertyMappingsForFeatureLine,
  updatePropertyMappingsForFeatureLine,
  duplicateFeatureLineSettings,
  createPropertyMappingsForFeatureLines,
  searchPropertyMappingsForFeatureLines,
} from '../../api/arcgis-civil3d-feature-line-setting.api';
import { ArcgisCivil3dFeatureLineSettingApiModel } from '../../../../types/models/arcgis-civil3d-feature-line-setting.model';
import { GetArcgisCivil3dFeatureLineSettingApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/feature-lines/get-arcgis-civil3d-feature-line-settings.request';
import { DeleteArcgisCivil3dFeatureLineSettingsApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/feature-lines/delete-arcgis-civil3d-feature-line-settings.request';
import { CreatePropertyMappingForFeatureLineApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/feature-lines/create-property-mapping-for-feature-line.request';
import { DeletePropertyMappingForFeatureLineApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/feature-lines/delete-property-mapping-for-feature-line.request';
import { GetAllPropertyMappingsForFeatureLineApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/feature-lines/get-all-property-mappings-for-feature-line.request';
import { UpdatePropertyMappingsForFeatureLineApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/feature-lines/update-property-mappings-for-feature-line.request';
import { DuplicateArcgisCivil3dFeatureLineSettingsApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/feature-lines/duplicate-arcgis-civil3d-feature-line-settings.request';
import { CreatePropertyMappingsForFeatureLinesApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/feature-lines/create-property-mappings-for-feature-lines.request';
import { SearchPropertyMappingsForFeatureLinesApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/feature-lines/search-property-mappings-for-feature-lines.request';

const baseQueryKey = 'feature-line-setting';
const searchQueryKey = 'search';
const propertyMappingQueryKey = 'property-mappings';

export const useUpdateFeatureLineSetting = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: UpdateArcgisCivil3dFeatureLineSettingApiRequest) =>
      (await updateFeatureLineSetting(params)).data,
    onSuccess: (featureLine) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseQueryKey &&
          (queryKey[1] === 'search' ||
            (queryKey[1] as UpdateArcgisCivil3dFeatureLineSettingApiRequest)?.configurationId ===
              featureLine.configuration?.id),
      }),
  });
  return [request, { ...result, featureLine: data }] as const;
};

export const useCreateFeatureLineSetting = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: CreateArcgisCivil3dFeatureLineSettingApiRequest) =>
      (await createFeatureLineSetting(params)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [baseQueryKey, searchQueryKey] }),
  });
  return [request, { ...result, featureLine: data }] as const;
};

export const useGetFeatureLineSetting = <T extends ArcgisCivil3dFeatureLineSettingApiModel>(
  params: GetArcgisCivil3dFeatureLineSettingApiRequest,
) => {
  const { data, ...result } = useAxiosQuery<T>({
    queryKey: [baseQueryKey, params],
    queryFn: async () => (await getFeatureLineSetting<T>(params)).data,
    enabled: Boolean(params.featureLineId && !Number.isNaN(params.featureLineId)),
  });
  return { ...result, featureLine: data } as const;
};

export const useDuplicateFeatureLineSettings = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: DuplicateArcgisCivil3dFeatureLineSettingsApiRequest) =>
      (await duplicateFeatureLineSettings(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, result] as const;
};

export const useDeleteFeatureLineSettings = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: DeleteArcgisCivil3dFeatureLineSettingsApiRequest) =>
      (await deleteFeatureLineSettings(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, result] as const;
};

export const useCreatePropertyMappingForFeatureLine = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: CreatePropertyMappingForFeatureLineApiRequest) =>
      (await createPropertyMappingForFeatureLine(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === propertyMappingQueryKey,
      }),
  });
  return [request, { ...result, ...data }] as const;
};

export const useCreatePropertyMappingsForFeatureLines = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: CreatePropertyMappingsForFeatureLinesApiRequest) =>
      (await createPropertyMappingsForFeatureLines(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, { ...result, ...data }] as const;
};

export const useSearchPropertyMappingsForFeatureLines = (params: SearchPropertyMappingsForFeatureLinesApiRequest) => {
  const { data, ...result } = useAxiosInfiniteQuery({
    queryKey: [baseQueryKey, searchQueryKey, params],
    queryFn: async ({ pageParam }) =>
      (await searchPropertyMappingsForFeatureLines({ cursor: pageParam, ...params })).data,
    initialPageParam: params.cursor,
    getNextPageParam: (lastResponse) => lastResponse.nextCursor,
  });
  return { ...result, response: data } as const;
};

export const useUpdatePropertyMappingsForFeatureLine = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: UpdatePropertyMappingsForFeatureLineApiRequest) =>
      (await updatePropertyMappingsForFeatureLine(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === propertyMappingQueryKey,
      }),
  });
  return [request, { ...result, ...data }] as const;
};

export const useGetAllPropertyMappingsForFeatureLine = (params: GetAllPropertyMappingsForFeatureLineApiRequest) => {
  const { data, ...result } = useAxiosQuery({
    queryKey: [baseQueryKey, params],
    queryFn: async () => (await getAllPropertyMappingsForFeatureLine(params)).data,
    enabled: Boolean(params.featureLineId && !Number.isNaN(params.featureLineId)),
  });
  return { ...result, ...data } as const;
};

export const useDeletePropertyMappingFromFeatureLine = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: DeletePropertyMappingForFeatureLineApiRequest) =>
      (await deletePropertyMappingFromFeatureLine(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === propertyMappingQueryKey,
      }),
  });
  return [request, result] as const;
};
